import React, { useState, useEffect, useCallback, useMemo } from 'react';
import GoogleMapReact from 'google-map-react';
import mixpanel from 'mixpanel-browser';

import {
  Grid,
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  InputLabel,
  TextField,
  Theme,
  Drawer,
  IconButton,
  Tooltip,
  Paper,
  Typography,
  Divider,
  Card,
} from '@material-ui/core';
import {
  Slider,
  Checkbox,
  Link,
  Modal,
  RadioGroup,
  FormControlLabel,
  Radio,
  Stack,
  Chip,
  CardContent,
} from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Alert from '@material-ui/lab/Alert';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import CircularProgress from '@material-ui/core/CircularProgress';
import LockSharpIcon from '@material-ui/icons/LockSharp';
import PinDropIcon from '@mui/icons-material/PinDrop';
import Collapse from '@mui/material/Collapse';

import {
  mapOptions,
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_PRIMARY_LIGHT,
  BACKGROUND_COLOR,
  COLOR_ACCENT_1,
  COLOR_RED,
  amenitiesObject,
  FILTER_FLOAT_BUTTON_Z_INDEX,
  exchangeRates,
  COLOR_RANGE_AIRBNB_MARKERS,
  COLOR_TOOLTIP_BACKGROUND,
} from 'src/const';
import { CompsetFilterVal, FilterPropTypes, FilterVal } from 'src/interfaces/iproptypes';
import { Amenities, TailoredRegionMarkers } from 'src/interfaces/idashboard';
import { TailoredRegionPointer } from './Pointer';
import {
  abbreviateNumber,
  convertCurrency,
  exportMarketOverview,
  exportSampleData,
  getQueryHistoryFilterParams,
} from 'src/utils/functions';
import { useDispatch } from 'react-redux';
import { COLOR_PRIMARY_1, COLOR_PRIMARY_2, KPI_CARD_COLOR } from 'src/colors';
import MapPointerRevRangeColorCard from './Cards/MapPointerRevRangeColorCard';
import { TooltipCard } from './Cards';
import { InfoTooltip } from './Tooltips';

mixpanel.init('0c9d3451de8e0a1a2f21ec23b05c2063');

const RIPPLE_SIZE = 100;
const RIPPLE_COLOR = COLOR_ACCENT_1;

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: 'auto',
    width: 'auto',
    background: BACKGROUND_COLOR,
  },
  filterContainer: {
    width: 'auto',
    padding: 20,
    [theme.breakpoints.down(900)]: {
      width: 300,
    },
    zIndex: FILTER_FLOAT_BUTTON_Z_INDEX,
  },
  selectObjInv: {
    width: 500,
    [theme.breakpoints.down(900)]: {
      width: 260,
      marginBottom: 20,
      marginRight: 0,
    },
  },
  selectObj: {
    width: 150,
    marginRight: 20,
    '&:last-child': {
      marginRight: 0,
    },
    [theme.breakpoints.down(900)]: {
      width: 220,
      marginBottom: 20,
      marginRight: 0,
    },
  },
  sliderObj: {
    width: 225,
    marginRight: 40,
    '&:last-child': {
      marginRight: 0,
    },
    [theme.breakpoints.down(900)]: {
      width: 220,
      marginBottom: 20,
      marginRight: 0,
    },
  },
  checkboxObj: {
    width: 490,
    [theme.breakpoints.down(900)]: {
      width: 220,
      marginBottom: 20,
    },
  },
  select: {
    '&:before': {
      borderColor: COLOR_PRIMARY,
    },
    '&:after': {
      borderColor: COLOR_PRIMARY,
    },
    '&:not(.Mui-disabled):hover::before': {
      borderColor: COLOR_PRIMARY,
    },
  },
  addFilterButton: {
    color: COLOR_PRIMARY_DARK,
    fontSize: 40,
  },
  paperModal: {
    backgroundColor: BACKGROUND_COLOR,
    width: 600,
    padding: theme.spacing(2),
  },
  mapContainer: {
    height: 400,
  },
  filterDivider: {
    width: 500,
    height: 1,
    background: COLOR_PRIMARY_DARK,
    margin: '10px 0',
    [theme.breakpoints.down(900)]: {
      width: 240,
    },
  },
  iconButton: {
    padding: 3,
  },
  tooltip: {
    backgroundColor: 'transparent',
    padding: 0,
    height: '80px',
  },
  ripple: {
    position: 'absolute',
    top: 0,
    left: 0,
    marginTop: -RIPPLE_SIZE / 2,
    marginLeft: -RIPPLE_SIZE / 2,
    width: RIPPLE_SIZE,
    height: RIPPLE_SIZE,
    animation: `$rippleAnimation 1000ms ${theme.transitions.easing.easeOut}`,
    animationIterationCount: 'infinite',
    opacity: 0,
    borderRadius: '50%',
    boxShadow: `0 0 6px 3px ${RIPPLE_COLOR}`,
  },
  '@keyframes rippleAnimation': {
    '0%': {
      transform: 'scale(0.1, 0.1)',
      opacity: 0,
    },
    '1%': {
      opacity: 1,
    },
    '50% ': {
      opacity: 1,
    },
    '100%': {
      transform: 'scale(1.2, 1.2)',
      opacity: 0,
    },
  },
}));

const buttonStyle = makeStyles((theme: Theme) => ({
  root: {
    background: COLOR_PRIMARY_DARK, //`radial-gradient(circle, hsla(189, 96%, 29%, 1) 0%, hsla(178, 100%, 38%, 1) 100%);`,
    borderRadius: 5,
    border: 0,
    color: 'white',
    padding: '10px 30px',
    boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
    '&:hover': {
      background: COLOR_PRIMARY_LIGHT,
    },
    fontSize: 18,
    marginLeft: 40,
    marginBottom: 20,
    width: '40%',
    minWidth: 300,
  },
  locationButton: {
    background: COLOR_PRIMARY_DARK, //`radial-gradient(circle, hsla(189, 96%, 29%, 1) 0%, hsla(178, 100%, 38%, 1) 100%);`,
    borderRadius: 5,
    border: 0,
    color: 'white',
    height: 40,
    width: 140,
    padding: '0 15px',
    boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
    '&:hover': {
      background: COLOR_PRIMARY_LIGHT,
    },
    marginLeft: 20,
  },
  modalButton: {
    background: COLOR_PRIMARY_DARK, //`radial-gradient(circle, hsla(189, 96%, 29%, 1) 0%, hsla(178, 100%, 38%, 1) 100%);`,
    borderRadius: 5,
    border: 0,
    color: 'white',
    height: 30,
    padding: '0 15px',
    boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
    '&:hover': {
      background: COLOR_PRIMARY_LIGHT,
    },
    display: 'flex',
    float: 'right',
  },
  resetBtn: {
    background: BACKGROUND_COLOR,
    borderRadius: 5,
    border: '2px solid',
    color: COLOR_PRIMARY,
    height: 30,
    padding: '0 15px',
    '&:hover': {
      background: BACKGROUND_COLOR,
    },
  },
}));

const FilterChip = ({
  label,
  value,
  variant,
  handleOnClick,
  styles,
  tooltipText,
}: {
  variant: 'filled' | 'outlined';
  label: string | number | JSX.Element;
  value: string | number;
  handleOnClick: (value: string | number) => void;
  styles?: any;
  tooltipText?: string;
}) => {
  const classes = useStyles();

  const chipComponent = (
    <Chip
      label={label}
      variant={variant}
      onClick={() => handleOnClick(value)}
      sx={{
        minWidth: 50,
        marginTop: '10px',
        marginRight: '5px',
        borderColor: KPI_CARD_COLOR,
        borderWidth: '1px',
        color: KPI_CARD_COLOR,
        fontWeight: 500,
        '&.MuiChip-filled': {
          backgroundColor: KPI_CARD_COLOR,
          color: '#fff',
        },
        ...styles,
      }}
    />
  );

  if (!tooltipText) {
    return chipComponent;
  }

  return (
    <Tooltip
      classes={{ tooltip: classes.tooltip }}
      title={
        <Card style={{ width: '160px', height: '60px' }}>
          <CardContent
            style={{ background: COLOR_TOOLTIP_BACKGROUND, height: '60px', fontSize: '18px' }}
          >
            <Grid container direction="column" alignItems="flex-start">
              <Typography variant="body1">{tooltipText ? tooltipText : ''}</Typography>
            </Grid>
          </CardContent>
        </Card>
      }
    >
      {chipComponent}
    </Tooltip>
  );
};

const Filter = ({
  listings,
  bookings,
  isDisabled,
  search,
  filterVal,
  setFilterVal,
  compsetFilterVal,
  setCompsetFilterVal,
  applyFilter,
  applyBookingFilter,
  open,
  toggleDrawer,
  dashboardState,
  setFilterTriggered,
  compsetListingIDs,
  compsetID,
  setIdle,
  setCurrentCompsetID,
  setCurrentCompsetName,
  matches,
  setFilterApplied,
  setSubscriptionModalOpen,
  mapTriggered,
  setMapTriggered,
  resetFilter,
  filterActive,
  setFilterActive,
  currency,
  multipleMarkersCallLoading,
  revPotentialRangeInvestment,
  setIndividualRevenuePotentialRange,
  handleOnSampleClick,
  isListingLoading,
}: FilterPropTypes) => {
  const buttonClass = buttonStyle();
  const classes = useStyles();

  const [selectedAmenity, setSelectedAmenity] = useState<string>('any');

  //loads the previously saved filters from local storage
  useEffect(() => {
    //loads the saved room type from local storage
    const room_type_from_localStorage = localStorage.getItem('room_type');

    const _splited_room_type = (room_type_from_localStorage
      ? room_type_from_localStorage.split(',')
      : []) as string[];

    if (room_type_from_localStorage) {
      setFilterVal((filterVal: FilterVal) => {
        return { ...filterVal, roomType: _splited_room_type };
      });
    }

    //loads the saved bedroom number from local storage
    const no_of_bedroom_from_localStorage = localStorage.getItem('bedroom_number');

    if (no_of_bedroom_from_localStorage) {
      setFilterVal((filterVal: FilterVal) => {
        return { ...filterVal, bedrooms: no_of_bedroom_from_localStorage.split(',').map(Number) };
      });
    }

    //loads the saved bathroom number from local storage
    const no_of_bathroom_from_localStorage = localStorage.getItem('bathroom_number');

    if (no_of_bathroom_from_localStorage) {
      setFilterVal((filterVal: FilterVal) => {
        return { ...filterVal, bathrooms: no_of_bathroom_from_localStorage.split(',').map(Number) };
      });
    }

    //loads the saved host type from local storage
    const host_type_from_localStorage = localStorage.getItem('host_type');

    if (host_type_from_localStorage) {
      setFilterVal((filterVal: FilterVal) => {
        return {
          ...filterVal,
          hostType: host_type_from_localStorage as 'independent' | 'professional' | 'both',
        };
      });
    }

    //loads the saved minimum nights number from local storage
    const minimum_nights_from_localStorage = localStorage.getItem('minimum_nights');

    if (minimum_nights_from_localStorage) {
      setFilterVal((filterVal: FilterVal) => {
        return {
          ...filterVal,
          minimum_nights: minimum_nights_from_localStorage.split(',').map(Number),
        };
      });
    }

    //loads the number of reviews from the local storage
    const number_of_reviews_from_localStorage = localStorage.getItem('number_of_reviews');

    if (number_of_reviews_from_localStorage) {
      let review: number | 'all' =
        number_of_reviews_from_localStorage !== 'all'
          ? Number(number_of_reviews_from_localStorage)
          : 'all';
      setFilterVal((filterVal: FilterVal) => {
        return {
          ...filterVal,
          number_of_reviews: review,
        };
      });
    }

    //loads the rating from localstorage
    const rating_from_localstorage = localStorage.getItem('rating');

    if (rating_from_localstorage) {
      const rating = rating_from_localstorage.split(',').map(Number);

      setFilterVal((filterVal: FilterVal) => {
        return {
          ...filterVal,
          rating: rating,
        };
      });
    }

    //loads the saved number of people can sleep from local storage
    const no_of_people_from_localStorage = localStorage.getItem('no_of_people');

    if (no_of_people_from_localStorage) {
      setFilterVal((filterVal: FilterVal) => {
        return {
          ...filterVal,
          accommodates: no_of_people_from_localStorage.split(',').map(Number),
        };
      });
    }

    //loads the saved amenities from local storage
    // let amenities_from_localStorage: string | object | null = localStorage.getItem('amenities');
    // if (amenities_from_localStorage) {
    //   amenities_from_localStorage = JSON.parse(amenities_from_localStorage);
    //   (amenities_from_localStorage as Amenities).all = false;

    //   setFilterVal((filterVal: FilterVal) => {
    //     return {
    //       ...filterVal,
    //       amenities: amenities_from_localStorage as Amenities,
    //     };
    //   });
    // }
  }, []);

  const handleColumnChange = (event: SelectChangeEvent<string>, index: number) => {
    setCompsetFilterVal((e: CompsetFilterVal[]) => {
      let tempFilter: CompsetFilterVal[] = [...e];
      tempFilter[index].column = event.target.value;
      return tempFilter;
    });
  };

  const handleOperatorChange = (event: SelectChangeEvent<string>, index: number) => {
    setCompsetFilterVal((e: CompsetFilterVal[]) => {
      let tempFilter: CompsetFilterVal[] = [...e];
      tempFilter[index].operator = event.target.value;
      return tempFilter;
    });
  };

  const handleValueChange = (
    event:
      | SelectChangeEvent<string | number>
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number
  ) => {
    setCompsetFilterVal((e: CompsetFilterVal[]) => {
      let tempFilter: CompsetFilterVal[] = [...e];
      tempFilter[index].value = event.target ? event.target.value : '';
      return tempFilter;
    });
  };

  const [compsetName, setCompsetName] = useState<null | string>(null);
  const handleCompsetNameChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setCompsetName(event.target.value);
    },
    []
  );
  const [compsetNameAlert, setCompsetNameAlert] = useState<boolean>(false);

  const [advancedFilterOpen, setAdvancedFilterOpen] = useState<boolean>(false);

  let columnTypes = {
    bedrooms: 'number',
    bathrooms: 'number',
    accommodates: 'number',
    room_type: 'string',
    host_name: 'string',
    hostListingCount: 'number',
    minimum_nights: 'number',
  };

  // Room Types
  const roomTypesObj =
    dashboardState.showInsightsDashboard || dashboardState.showHistoricalDashboard
      ? {
          entire_home: 'Entire House',
          condo: 'Apartment',
          private_room: 'Private Room',
          shared_room: 'Shared Room',
          hotel_room: 'Hotel Room',
          // all: 'Show all listings',
        }
      : {
          entire_home: 'Entire House',
          private_room: 'Private Room',
          shared_room: 'Shared Room',
          hotel_room: 'Hotel Room',
          // all: 'Show all listings',
        };

  const handleRoomTypeChange = useCallback((value: string) => {
    setFilterVal((filterVal: FilterVal) => {
      let _room_type = filterVal.roomType;

      if (value !== 'all' && _room_type.includes('all')) {
        _room_type.splice(_room_type.indexOf('all'), 1);
      }

      if (_room_type.includes(value)) {
        _room_type = _room_type.filter(data => {
          return data !== value;
        });
      } else {
        _room_type.push(value);
      }

      if (value === 'all') {
        _room_type = ['all'];
      }

      let new_bedroom_val = filterVal.bedrooms;
      let new_bathroom_val = filterVal.bathrooms;

      if (
        !_room_type.includes('entire_home') &&
        !_room_type.includes('condo') &&
        !_room_type.includes('all')
      ) {
        new_bedroom_val = [100];
        new_bathroom_val = [100];
        localStorage.setItem('bedroom_number', new_bedroom_val.toString());
        localStorage.setItem('bathroom_number', new_bathroom_val.toString());
      }

      // Removing room_type value all from localstorage as we don't show the option all anymore
      // if (_room_type.includes('all')) {
      //   _room_type.splice(_room_type.indexOf('all'), 1);
      // }

      localStorage.setItem('room_type', _room_type.toString());

      return {
        ...filterVal,
        roomType: _room_type,
        bedrooms: new_bedroom_val as number[],
        bathrooms: new_bathroom_val,
      };
    });
  }, []);

  const handleBedroomsChange = (value: string | number) => {
    if (
      isNaN(Number(value)) &&
      !['private_room', 'hotel_room', 'shared_room'].includes((value as unknown) as string)
    ) {
      return null;
    }

    let newValue = filterVal.bedrooms;

    if (value !== 100 && newValue.includes(100)) {
      newValue.splice(newValue.indexOf(100), 1);
    }

    if (newValue.includes(value)) {
      newValue = newValue.filter(val => val !== value);
    } else {
      newValue.push(value);
    }

    if (value === 100) {
      newValue = [100];
    }

    // Removing bedroom value all from localstorage as we don't show the option all anymore
    // if (newValue.includes(100)) {
    //   newValue.splice(newValue.indexOf(100), 1);
    // }

    const _room_type = filterVal.roomType;

    localStorage.setItem('bedroom_number', newValue.toString());
    setFilterVal((filterVal: FilterVal) => {
      return { ...filterVal, bedrooms: newValue as number[] };
    });

    return null;
  };
  const handleBathroomsChange = (value: string | number) => {
    value = Number(value);
    if (isNaN(value)) {
      return null;
    }

    let newValue = filterVal.bathrooms;

    if (value !== 100 && newValue.includes(100)) {
      newValue.splice(newValue.indexOf(100), 1);
    }
    if (newValue.includes(value)) {
      newValue = newValue.filter(val => val !== value);
    } else {
      newValue.push(value);
    }

    if (value === 100) {
      newValue = [100];
    }

    // Removing bathroom value all from localstorage as we don't show the option all anymore
    // if (newValue.includes(100)) {
    //   newValue.splice(newValue.indexOf(100), 1);
    // }

    localStorage.setItem('bathroom_number', newValue.toString());
    setFilterVal((filterVal: FilterVal) => {
      return { ...filterVal, bathrooms: newValue as number[] };
    });

    return null;
  };

  const handleRevenueFilter = (value: number | 'all') => {
    if (isDisabled) return;

    if (value === 'all') {
      return setFilterVal((filterVal: FilterVal) => {
        return { ...filterVal, revenueFilter: ['all'] };
      });
    }

    if (isNaN(Number(value))) return null;

    let newRevenueFilterValueArr = filterVal.revenueFilter;

    if (newRevenueFilterValueArr.includes('all')) {
      newRevenueFilterValueArr.splice(newRevenueFilterValueArr.indexOf('all'), 1);
    }

    const tempRevPotentialRange = { ...revPotentialRangeInvestment };
    if (newRevenueFilterValueArr.length < 1) {
      Object.keys(tempRevPotentialRange).forEach(revPotential => {
        tempRevPotentialRange[revPotential] = revPotential === String(value) ? 'visible' : 'hidden';
      });
    } else {
      if (tempRevPotentialRange[value] === 'visible') {
        tempRevPotentialRange[value] = 'hidden';
      } else {
        tempRevPotentialRange[value] = 'visible';
      }
    }
    setIndividualRevenuePotentialRange(tempRevPotentialRange);

    if (newRevenueFilterValueArr.includes(value)) {
      newRevenueFilterValueArr.splice(newRevenueFilterValueArr.indexOf(value), 1);
    } else {
      newRevenueFilterValueArr.push(value);
    }

    setFilterVal((filterVal: FilterVal) => {
      return { ...filterVal, revenueFilter: newRevenueFilterValueArr as (number | 'all')[] };
    });

    return null;
  };

  const handleHostTypeChange = (value: 'independent' | 'professional' | 'both') => {
    localStorage.setItem('host_type', value);
    setFilterVal((filterVal: FilterVal) => {
      return { ...filterVal, hostType: value };
    });
  };

  // Minimum Nights
  const handleMinimumNightsChange = (event: Event, newValue: number | number[]) => {
    newValue = newValue as number;
    localStorage.setItem('minimum_nights', newValue.toString());
    setFilterVal((filterVal: FilterVal) => {
      return { ...filterVal, minimum_nights: newValue as number[] };
    });
  };
  const minimumNightsMarks = [
    {
      value: 1,
      label: '1',
    },
    {
      value: 30,
      label: '30+',
    },
  ];

  //Number of Reviews
  const number_of_reviews_marks = [
    {
      value: 0,
      label: '0',
    },
    {
      value: 1000,
      label: '1k+',
    },
  ];

  //Rating
  const rating_marks = [
    {
      value: 3.9,
      label: '<4.0',
    },
    {
      value: 5.0,
      label: '5.0',
    },
  ];

  const handleNumberOfReviewsChange = (value: number | 'all') => {
    localStorage.setItem('number_of_reviews', value.toString());
    setFilterVal((filterVal: FilterVal) => {
      return { ...filterVal, number_of_reviews: value };
    });
  };

  const handleRatingChange = (event: Event, newValue: number | number[]) => {
    localStorage.setItem('rating', newValue.toString());
    setFilterVal((filterVal: FilterVal) => {
      return { ...filterVal, rating: newValue as number[] };
    });
  };

  // Accomodates
  const handleAccomodatesChange = (event: Event, newValue: number | number[]) => {
    // newValue = [1, newValue as number];
    localStorage.setItem('no_of_people', newValue.toString());
    setFilterVal((filterVal: FilterVal) => {
      return { ...filterVal, accommodates: newValue as number[] };
    });
  };
  const accomodatesMarks = [
    {
      value: 1,
      label: '1',
    },
    {
      value: 16,
      label: '16+',
    },
  ];

  // Amenities
  const handleAmenitiesChange = (event: SelectChangeEvent<string>) => {
    setFilterVal((prevFilterVal: FilterVal) => {
      const value = event.target.value;
      setSelectedAmenity(value);

      const newAmenitiesObject = { ...filterVal.amenities };

      Object.keys(amenitiesObject).forEach(amenity => {
        newAmenitiesObject[amenity] = value == 'any' ? false : amenity == value ? true : false;
      });

      localStorage.setItem(
        'amenities',
        JSON.stringify({
          ...newAmenitiesObject,
        })
      );

      return {
        ...filterVal,
        amenities: newAmenitiesObject,
      };
    });
  };

  const [showAllBookingComListingsChecked, setShowAllBookingComListingsChecked] = useState(
    filterVal.showAllBookingComListings
  );

  const handleShowAllBookingcomListingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowAllBookingComListingsChecked((e: boolean) => {
      setFilterVal((filterVal: FilterVal) => {
        return { ...filterVal, showAllBookingComListings: event.target.checked };
      });
      return event.target.checked;
    });
  };

  if (dashboardState.showPricingDashboard) {
    columnTypes = { ...columnTypes, ...{ coordinate: 'string' } };
  }

  const addFilter = useCallback(() => {
    setCompsetFilterVal((e: CompsetFilterVal[]) => {
      let tempFilter: CompsetFilterVal[] = [...e];
      if (e.length < 7) {
        tempFilter.push({
          id: e.length,
          column: 'room_type',
          operator: '=',
          value: 'entire_home',
        });
      }
      return tempFilter;
    });
  }, []);

  const removeFilter = useCallback((event, index) => {
    setCompsetFilterVal((e: CompsetFilterVal[]) => {
      let tempFilter: CompsetFilterVal[] = [];
      e.map((filter, i) => {
        if (filter.id != index) {
          tempFilter.push(filter);
        }
      });
      return tempFilter;
    });
  }, []);

  const triggerFilter = () => {
    if (dashboardState.showInsightsDashboard || dashboardState.showHistoricalDashboard) {
      // console.log('[DEBUG]: ', listings);
      if (listings && listings.data) {
        setFilterActive(true);
        applyFilter(revPotentialRangeInvestment);
        setFilterApplied(true);
      }
    }
    if (dashboardState.showPricingDashboard) {
      if (compsetName) {
        applyBookingFilter(compsetName);
        setFilterTriggered(true);
        setCompsetNameAlert(false);
        setCurrentCompsetName('');
        setCurrentCompsetID('');
      } else {
        setCompsetNameAlert(true);
      }
      setIdle(false);
    }
  };

  const [coordIndex, setCoordIndex] = useState<number>(0);

  useEffect(() => {
    if (dashboardState.showPricingDashboard && !compsetID && !matches) setMapTriggered(true);
  }, [dashboardState.showPricingDashboard]);

  const handleOpen = (index: number) => {
    setMapTriggered(true);
    setCoordIndex(index);
  };
  const handleClose = () => {
    setMapTriggered(false);
  };

  const removeDrawingTool = drawingManager => {
    drawingManager.setDrawingMode(null);
    drawingManager.setOptions({
      drawingControl: false,
    });
  };

  const [customPolygon, setCustomPolygon] = useState<number[][]>([] as number[][]);

  const apiIsLoaded = (map, maps) => {
    const drawingManager = new maps.drawing.DrawingManager({
      drawingControl: true,
      drawingControlOptions: {
        position: maps.ControlPosition.TOP_CENTER,
        drawingModes: [maps.drawing.OverlayType.POLYGON],
      },
      polygonOptions: {
        editable: false,
        draggable: true,
      },
    });

    drawingManager.setMap(map);

    // get paths of polygona and set them to state
    maps.event.addListener(drawingManager, 'polygoncomplete', polygon => {
      removeDrawingTool(drawingManager);
      let polygonArray: number[][] = [];
      let polygonPaths = polygon.getPath().getArray();
      polygonPaths.forEach(path => {
        polygonArray.push([path.lat(), path.lng()]);
      });
      setCustomPolygon(polygonArray);

      maps.event.addListener(polygon, 'dragend', () => {
        polygonArray = [];
        polygonPaths = polygon.getPath().getArray();
        polygonPaths.forEach(path => {
          polygonArray.push([path.lat(), path.lng()]);
        });
        setCustomPolygon(polygonArray);
      });
    });
  };

  const handleMapChange = useCallback(({ bounds }) => {
    const centerLat = (bounds.sw.lat + bounds.ne.lat) / 2;
    setCenterLat(centerLat);
    const centerLng = (bounds.sw.lng + bounds.ne.lng) / 2;
    setCenterLng(centerLng);
  }, []);

  const [locationTriggered, setLocationTriggered] = useState<boolean>(false);
  const setLocation = () => {
    setCompsetFilterVal((e: CompsetFilterVal[]) => {
      let tempFilter: CompsetFilterVal[] = [...e];
      setCustomPolygon((e: number[][]) => {
        let polygonString: string = '';
        e.forEach((polygon, i) => {
          polygonString += `(${polygon.toString().replace(',', ' ')}),`;
        });
        polygonString = polygonString.slice(0, -1);
        tempFilter[coordIndex].value = polygonString;
        return e;
      });
      return tempFilter;
    });
    setMapTriggered(false);
    setLocationTriggered(true);
  };

  const [centerLat, setCenterLat] = useState<number>(39.210878338011376);
  const [centerLng, setCenterLng] = useState<number>(-101.51684523289367);
  const [homeLat, setHomeLat] = useState<number>(39.210878338011376);
  const [homeLng, setHomeLng] = useState<number>(-101.51684523289367);
  const [locationAllowed, setLocationAllowed] = useState<boolean>(false);

  const locationPin = useMemo(() => {
    return (
      <div>
        <div className={classes.ripple}></div>
        <PinDropIcon
          style={{
            fontSize: 40,
            position: 'absolute',
            top: -20,
            left: -20,
          }}
        />
      </div>
    );
  }, [centerLat, centerLng]);

  let markers = useMemo(() => {
    if (!bookings.loading && bookings.data.length > 0) {
      return bookings.data.map((item: TailoredRegionMarkers, index: number) => (
        <TailoredRegionPointer
          key={index}
          lat={item.latitude}
          lng={item.longitude}
          data={{
            latitude: item.latitude as number,
            longitude: item.longitude as number,
            listingID: item.listingID as number,
            room_type: item.room_type as string,
          }}
        />
      ));
    }
    return;
  }, [bookings.data]);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      position => {
        setHomeLat(position.coords.latitude);
        setHomeLng(position.coords.longitude);
        setCenterLat(position.coords.latitude);
        setCenterLng(position.coords.longitude);
        setLocationAllowed(true);
      },
      error => {
        console.error('Error Code = ' + error.code + ' - ' + error.message);
      }
    );
  }, []);

  //get the amenities list
  const amenitiesPresent: string[] = [];
  for (const key in filterVal.amenities) {
    filterVal.amenities[key] && amenitiesPresent.push(key);
  }
  let amenitiesText: string = '';
  if (amenitiesPresent.length === 1) {
    amenitiesText += amenitiesPresent[0];
  } else if (amenitiesPresent.length === 2) {
    amenitiesText += amenitiesPresent[0] + ' and ' + amenitiesPresent[1];
  } else if (amenitiesPresent.length > 2) {
    for (let i = 0; i < amenitiesPresent.length; i++) {
      if (i === amenitiesPresent.length - 1) {
        amenitiesText += 'and ' + amenitiesPresent[i];
      } else {
        amenitiesText += amenitiesPresent[i] + ', ';
      }
    }
  }

  const CustomAlert = ({
    type,
    color,
    body,
    pushLeft,
  }: {
    type: 'info' | 'warning' | 'error';
    color: string;
    body: JSX.Element | string;
    pushLeft: boolean;
  }) => {
    const style = {
      width: matches ? 300 : 500,
    };

    if (pushLeft) {
      style['marginLeft'] = 0;
      style['paddingLeft'] = 0;
    }

    return (
      <Grid
        container
        item
        direction="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        className={classes.filterContainer}
        style={style}
      >
        <Alert severity={type}>
          <Typography variant="body2">{body}</Typography>
        </Alert>
      </Grid>
    );
  };

  const isNotEntireHouseOrCondo: boolean =
    !filterVal.roomType.includes('entire_home') &&
    !filterVal.roomType.includes('condo') &&
    !filterVal.roomType.includes('all');

  const isNoListingAvailable =
    isNotEntireHouseOrCondo &&
    (!filterVal.bedrooms.includes(100) ||
      filterVal.bedrooms.length > 1 ||
      !filterVal.bathrooms.includes(100) ||
      filterVal.bathrooms.length > 1);

  return (
    <>
      <Drawer variant="temporary" anchor="right" open={open} onClose={toggleDrawer}>
        <Grid container direction="column" id="filter" className={classes.root}>
          {/* Investment Dashboard Filter */}

          {(dashboardState.showInsightsDashboard || dashboardState.showHistoricalDashboard) && (
            <Grid
              container
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-start"
              className={classes.filterContainer}
            >
              <Grid
                container
                direction="column"
                justifyContent="flex-start"
                alignItems="flex-start"
                className={classes.filterContainer}
                style={{ marginTop: -20 }}
              >
                <Grid
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Typography
                    variant="h5"
                    style={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                  >
                    Filter
                  </Typography>

                  {(dashboardState.showInsightsDashboard ||
                    dashboardState.showHistoricalDashboard) && (
                    <Button
                      className={buttonClass.resetBtn}
                      size="small"
                      variant="outlined"
                      onClick={() => !isDisabled && resetFilter()}
                    >
                      Reset
                    </Button>
                  )}
                </Grid>
                <Divider className={classes.filterDivider} />
              </Grid>

              <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="flex-start"
                className={classes.filterContainer}
              >
                <Grid item className={classes.selectObjInv}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <Typography
                      color="textSecondary"
                      style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                    >
                      Property Type
                    </Typography>
                    <Grid xs={12} container justifyContent="flex-start" style={{ gap: '5px' }}>
                      <FilterChip
                        value={'all'}
                        label={'Any'}
                        handleOnClick={() => handleRoomTypeChange('all')}
                        variant={filterVal.roomType.includes('all') ? 'filled' : 'outlined'}
                      />
                      {['entire_home', 'condo'].map((roomType, index) => (
                        <FilterChip
                          value={roomType}
                          label={roomTypesObj[roomType]}
                          handleOnClick={() => handleRoomTypeChange(roomType)}
                          variant={
                            !filterVal.roomType.includes(roomType) ||
                            filterVal.roomType.includes('all')
                              ? 'outlined'
                              : 'filled'
                          }
                        />
                      ))}
                    </Grid>
                  </FormControl>
                </Grid>
              </Grid>

              <Grid
                container
                direction="column"
                justifyContent="flex-start"
                alignItems="flex-start"
                className={classes.filterContainer}
                style={{ width: '100%' }}
              >
                <Grid item className={classes.selectObjInv}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <Typography
                      color="textSecondary"
                      style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                    >
                      Bedrooms
                    </Typography>
                    {isNotEntireHouseOrCondo && (
                      <CustomAlert
                        color="inherit"
                        type="warning"
                        pushLeft={true}
                        body={
                          'Bedroom filter can only be applied to "Entire House", "Apartment (Condo)" and "Show all listings".'
                        }
                      />
                    )}
                    <Grid
                      xs={12}
                      item
                      container
                      justifyContent="flex-start"
                      style={{ width: '100%', gap: '5px' }}
                    >
                      <FilterChip
                        value={100}
                        label={'Any'}
                        handleOnClick={() => handleBedroomsChange(100)}
                        variant={filterVal.bedrooms.includes(100) ? 'filled' : 'outlined'}
                      />
                      {[0, 1, 2, 3, 4, 5, 6, 'private_room', 'shared_room', 'hotel_room'].map(
                        (bedRoomType, index) => (
                          <FilterChip
                            value={bedRoomType}
                            label={
                              typeof bedRoomType === typeof ''
                                ? bedRoomType[0].toUpperCase()
                                : bedRoomType === 0
                                ? 'Studio'
                                : bedRoomType === 6
                                ? '6+'
                                : bedRoomType
                            }
                            handleOnClick={() => handleBedroomsChange(bedRoomType)}
                            variant={
                              !filterVal.bedrooms.includes(bedRoomType) ||
                              filterVal.bedrooms.includes(100)
                                ? 'outlined'
                                : 'filled'
                            }
                            tooltipText={
                              roomTypesObj[bedRoomType] ? roomTypesObj[bedRoomType] : undefined
                            }
                          />
                        )
                      )}
                    </Grid>
                  </FormControl>
                </Grid>
              </Grid>

              <Grid
                container
                direction="column"
                justifyContent="flex-start"
                alignItems="flex-start"
                className={classes.filterContainer}
                style={{ width: '100%' }}
              >
                <Grid item className={classes.selectObjInv}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <Typography
                      color="textSecondary"
                      style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                    >
                      Bathrooms
                    </Typography>
                    {isNotEntireHouseOrCondo && (
                      <CustomAlert
                        color="inherit"
                        type="warning"
                        pushLeft={true}
                        body={
                          'Bathroom filter can only be applied to "Entire House", "Apartment (Condo)" and "Show all listings".'
                        }
                      />
                    )}
                    <Grid xs={12} container justifyContent="flex-start" style={{ gap: '5px' }}>
                      <FilterChip
                        value={100}
                        label={'Any'}
                        handleOnClick={() => handleBathroomsChange(100)}
                        variant={filterVal.bathrooms.includes(100) ? 'filled' : 'outlined'}
                      />
                      {[0, 1, 2, 3, 4, 5, 6].map((bathRoomType, index) => (
                        <FilterChip
                          value={bathRoomType}
                          label={
                            bathRoomType === 6 ? '6+' : bathRoomType === 0 ? 'S' : bathRoomType
                          }
                          handleOnClick={() => handleBathroomsChange(bathRoomType)}
                          variant={
                            !filterVal.bathrooms.includes(bathRoomType) ||
                            filterVal.bathrooms.includes(100)
                              ? 'outlined'
                              : 'filled'
                          }
                          tooltipText={bathRoomType === 0 ? 'Shared Bathroom' : undefined}
                        />
                      ))}
                    </Grid>
                  </FormControl>
                </Grid>
              </Grid>

              {/* review filter */}
              <Grid
                container
                direction="column"
                justifyContent="flex-start"
                alignItems="flex-start"
                className={classes.filterContainer}
                style={{ width: '100%' }}
              >
                <Typography
                  color="textSecondary"
                  style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                >
                  Number of Reviews
                </Typography>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    defaultValue={filterVal.number_of_reviews}
                    value={filterVal.number_of_reviews}
                    onChange={e => {
                      handleNumberOfReviewsChange(e.target.value as number | 'all');
                    }}
                  >
                    <FormControlLabel
                      value="all"
                      control={<Radio style={{ color: COLOR_PRIMARY }} />}
                      label="Any"
                    />
                    <FormControlLabel
                      value={1}
                      control={<Radio style={{ color: COLOR_PRIMARY }} />}
                      label="1+"
                    />
                    <FormControlLabel
                      value={3}
                      control={<Radio style={{ color: COLOR_PRIMARY }} />}
                      label="3+"
                    />
                    <FormControlLabel
                      value={10}
                      control={<Radio style={{ color: COLOR_PRIMARY }} />}
                      label="10+"
                    />
                    <FormControlLabel
                      value={30}
                      control={<Radio style={{ color: COLOR_PRIMARY }} />}
                      label="30+"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Collapse in={advancedFilterOpen} timeout="auto">
                <Grid
                  container
                  direction="column"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  className={classes.filterContainer}
                  style={{ width: '100%' }}
                >
                  <Grid item className={classes.selectObjInv}>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Typography
                        color="textSecondary"
                        style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                      >
                        Revenue Filter
                      </Typography>
                      <Grid xs={12} container justifyContent="flex-start" style={{ gap: '5px' }}>
                        <FilterChip
                          value={'all'}
                          label={'Any'}
                          handleOnClick={() => handleRevenueFilter('all')}
                          variant={filterVal.revenueFilter.includes('all') ? 'filled' : 'outlined'}
                        />
                        {Object.keys(revPotentialRangeInvestment)
                          .map(Number)
                          .sort((a, b) => a - b)
                          .map((revPotential, index) => {
                            return (
                              <FilterChip
                                value={revPotential}
                                label={
                                  <>
                                    <Typography
                                      variant="caption"
                                      component="p"
                                      style={{
                                        filter: isDisabled ? 'blur(6px)' : 'blur(0)',
                                        fontWeight: 'bold',
                                      }}
                                    >
                                      {'<='} {exchangeRates[currency].symbol}{' '}
                                      {abbreviateNumber(convertCurrency(currency, revPotential))}
                                    </Typography>
                                  </>
                                }
                                handleOnClick={() => {
                                  handleRevenueFilter(revPotential);
                                }}
                                variant={
                                  revPotentialRangeInvestment[revPotential] === 'visible' &&
                                  !filterVal.revenueFilter.includes('all')
                                    ? 'filled'
                                    : 'outlined'
                                }
                                styles={{
                                  backgroundColor: 'transparent',
                                  borderColor: `${COLOR_RANGE_AIRBNB_MARKERS[index]}`,
                                  color: `#000`,
                                  '&.MuiChip-filled': {
                                    backgroundColor: `${COLOR_RANGE_AIRBNB_MARKERS[index]}60`,
                                    border: `1px solid ${COLOR_RANGE_AIRBNB_MARKERS[index]}`,
                                  },
                                }}
                              />
                            );
                          })}
                      </Grid>
                    </FormControl>
                  </Grid>
                </Grid>

                <Grid
                  container
                  direction="column"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  className={classes.filterContainer}
                  style={{ width: '100%' }}
                >
                  <Typography
                    color="textSecondary"
                    style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                  >
                    Professionally Managed
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="row-radio-buttons-group"
                      defaultValue="both"
                      value={filterVal.hostType}
                      onChange={e => {
                        handleHostTypeChange(
                          e.target.value as 'independent' | 'professional' | 'both'
                        );
                      }}
                    >
                      <FormControlLabel
                        value="professional"
                        control={<Radio style={{ color: COLOR_PRIMARY }} />}
                        label="Yes"
                      />
                      <FormControlLabel
                        value="independent"
                        control={<Radio style={{ color: COLOR_PRIMARY }} />}
                        label="No"
                      />
                      <FormControlLabel
                        value="both"
                        control={<Radio style={{ color: COLOR_PRIMARY }} />}
                        label="Both"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid
                  container
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  className={classes.filterContainer}
                  style={{ width: '100%' }}
                >
                  <Typography
                    color="textSecondary"
                    style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                  >
                    Sleeps
                  </Typography>
                  <Slider
                    size="small"
                    value={filterVal.accommodates}
                    min={1}
                    max={16}
                    step={1}
                    marks={accomodatesMarks}
                    onChange={handleAccomodatesChange}
                    valueLabelDisplay="auto"
                    sx={{
                      color: COLOR_PRIMARY,
                    }}
                  />
                </Grid>

                <Grid
                  container
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  className={classes.filterContainer}
                  style={{ width: '100%' }}
                >
                  <Typography
                    color="textSecondary"
                    style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                  >
                    Minimum Stay
                  </Typography>
                  <Slider
                    size="small"
                    value={filterVal.minimum_nights}
                    min={1}
                    max={30}
                    marks={minimumNightsMarks}
                    onChange={handleMinimumNightsChange}
                    valueLabelDisplay="auto"
                    sx={{
                      color: COLOR_PRIMARY,
                    }}
                  />
                </Grid>

                <Grid
                  container
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  className={classes.filterContainer}
                  style={{ width: '100%' }}
                >
                  <Typography
                    color="textSecondary"
                    style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                  >
                    Rating
                  </Typography>
                  <Slider
                    size="small"
                    value={filterVal.rating}
                    min={3.9}
                    max={5}
                    step={0.1}
                    marks={rating_marks}
                    onChange={handleRatingChange}
                    valueLabelDisplay="auto"
                    valueLabelFormat={x => (x === 3.9 ? '<4.0' : x)}
                    sx={{
                      color: COLOR_PRIMARY,
                    }}
                  />
                </Grid>

                <Grid
                  container
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  className={classes.filterContainer}
                >
                  <Grid item className={classes.selectObjInv}>
                    {/* <FormControl variant="outlined" size="small" fullWidth>
                      <Typography
                        color="textSecondary"
                        style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                      >
                        Amenities
                      </Typography>
                      <Grid xs={12} container justifyContent="space-between">
                        {Object.entries(amenitiesObject).map(([key, value]) => (
                          <Grid item xs={12} sm={6}>
                            <Checkbox
                              checked={filterVal.amenities[key]}
                              onChange={handleAmenitiesChange}
                              value={value}
                              name={key}
                              id={'amenities' + key}
                              style={{ color: COLOR_PRIMARY }}
                            />
                            <FormLabel id={'amenities' + key} style={{ color: '#000' }}>
                              {value}
                            </FormLabel>
                          </Grid>
                        ))}
                      </Grid>
                      <Grid xs={12} style={{ marginTop: '20px' }}>
                        {amenitiesPresent.length > 0 && (
                          <Typography style={{ fontSize: '16px' }}>
                            **It will show listings that has {amenitiesText}.
                          </Typography>
                        )}
                      </Grid>
                    </FormControl> */}
                    <FormControl variant="outlined" size="small" fullWidth>
                      <Typography
                        color="textSecondary"
                        style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                      >
                        Amenities
                      </Typography>
                      <Select
                        value={selectedAmenity}
                        onChange={e => handleAmenitiesChange(e)}
                        sx={{
                          marginTop: '8px',
                          borderColor: COLOR_PRIMARY,
                          fontSize: '18px',
                          border: '1px solid' + COLOR_PRIMARY,
                          width: '60%',
                          '& .MuiSelect-select': {
                            color: '#000',
                            fontWeight: '500',
                            border: '1px solid' + COLOR_PRIMARY,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: '1px solid' + COLOR_PRIMARY,
                          },
                        }}
                      >
                        {[['any', 'Any'], ...Object.entries(amenitiesObject)].map(
                          ([key, value]) => (
                            <MenuItem key={key} value={key}>
                              {value}
                            </MenuItem>
                          )
                        )}
                      </Select>
                      {/* <Grid xs={12} style={{ marginTop: '20px' }}>
                        {selectedAmenity && selectedAmenity !== 'any' && (
                          <Typography style={{ fontSize: '16px' }}>
                            **It will show listings that have {amenitiesObject[selectedAmenity]}.
                          </Typography>
                        )}
                      </Grid> */}
                    </FormControl>
                  </Grid>
                </Grid>

                <Grid
                  container
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                  className={classes.filterContainer}
                  style={{ width: '100%' }}
                >
                  <Typography
                    color="textSecondary"
                    style={{ fontSize: '20px', fontWeight: 'bold', color: '#000' }}
                  >
                    Booking.com
                  </Typography>
                  <Grid container direction="row" alignItems="center">
                    <Checkbox
                      checked={showAllBookingComListingsChecked}
                      onChange={handleShowAllBookingcomListingsChange}
                      id={'booking.com-show-all'}
                      style={{ color: COLOR_PRIMARY }}
                    />
                    <FormLabel id={'booking.com-show-all'} style={{ color: '#000' }}>
                      Show booking.com listings
                    </FormLabel>
                  </Grid>
                </Grid>

                <Grid
                  container
                  direction="row"
                  justifyContent="flex-start"
                  className={classes.filterContainer}
                  style={{ width: '100%', marginTop: '-40px' }}
                >
                  <Typography variant="caption">
                    (All booking.com listings will be shown regardless of other filters you
                    selected)
                  </Typography>
                </Grid>
              </Collapse>

              <Typography
                style={{
                  fontSize: '18px',
                  color: '#000',
                  fontWeight: 'bold',
                  marginLeft: 'auto',
                  cursor: 'pointer',
                  textDecoration: advancedFilterOpen ? 'underline' : 'none',
                  marginTop: '25px',
                  marginBottom: '25px',
                }}
                onClick={() => setAdvancedFilterOpen(prev => !prev)}
              >
                {!advancedFilterOpen ? <>Advance filter &darr;</> : <>Simple filter &uarr;</>}
              </Typography>
            </Grid>
          )}

          {/* CompSet Filter */}

          {dashboardState.showPricingDashboard && (
            <Grid item className={classes.filterContainer}>
              <FormControl variant="outlined" size="small" fullWidth>
                <TextField
                  variant="outlined"
                  id="compsetName"
                  label="CompSet Name"
                  name="compsetName"
                  type="text"
                  size="small"
                  placeholder="Beachfront Studio"
                  required
                  onChange={e => handleCompsetNameChange(e)}
                />
              </FormControl>
            </Grid>
          )}

          {dashboardState.showPricingDashboard &&
            compsetFilterVal.map((filter: CompsetFilterVal, index: number) => (
              <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                key={filter.id}
                className={classes.filterContainer}
              >
                <Grid item className={classes.selectObj}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel id="columns">Columns</InputLabel>
                    <Select
                      value={filter.column}
                      labelId="columns"
                      id="columns"
                      label="Columns"
                      size="small"
                      onChange={e => handleColumnChange(e, index)}
                    >
                      <MenuItem value="bedrooms">Number of Bedrooms</MenuItem>
                      <MenuItem value="bathrooms">Number of Bathrooms</MenuItem>
                      <MenuItem value="accommodates">Capacity</MenuItem>
                      <MenuItem value="room_type">Type of Room</MenuItem>
                      <MenuItem value="host_name">Host Name</MenuItem>
                      <MenuItem value="hostListingCount">Number of listings owned by host</MenuItem>
                      <MenuItem value="minimum_nights">Minimum Stay</MenuItem>
                      {dashboardState.showPricingDashboard && (
                        <MenuItem value="coordinate">Location</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item className={classes.selectObj}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel id="operators">Operators</InputLabel>
                    {columnTypes[filter.column] === 'string' && (
                      <Select
                        value={filter.operator}
                        labelId="operators"
                        id="operators"
                        label="Operators"
                        size="small"
                        onChange={e => handleOperatorChange(e, index)}
                      >
                        {filter.column != 'coordinate' && (
                          <MenuItem value="equals">equals</MenuItem>
                        )}
                        {filter.column != 'coordinate' && (
                          <MenuItem value="contains">contains</MenuItem>
                        )}
                        {filter.column === 'coordinate' && <MenuItem value="=">=</MenuItem>}
                      </Select>
                    )}
                    {columnTypes[filter.column] === 'dropdown' && (
                      <Select
                        value={filter.operator}
                        labelId="operators"
                        id="operators"
                        label="Operators"
                        size="small"
                        onChange={e => handleOperatorChange(e, index)}
                      >
                        <MenuItem value="=">=</MenuItem>
                      </Select>
                    )}
                    {columnTypes[filter.column] === 'number' && (
                      <Select
                        value={filter.operator}
                        labelId="operators"
                        id="operators"
                        label="Operators"
                        size="small"
                        onChange={e => handleOperatorChange(e, index)}
                      >
                        <MenuItem value="=">=</MenuItem>
                        <MenuItem value=">">{`>`}</MenuItem>
                        <MenuItem value="<">{`<`}</MenuItem>
                        <MenuItem value="!=">!=</MenuItem>
                      </Select>
                    )}
                  </FormControl>
                </Grid>

                <Grid item className={classes.selectObj}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    {/* <InputLabel id="value">Value</InputLabel> */}
                    {filter.column === 'room_type' && (
                      <Select
                        value={filter.value}
                        labelId="value"
                        id="value"
                        label="Value"
                        size="small"
                        onChange={e => handleValueChange(e, index)}
                      >
                        {Object.keys(roomTypesObj).map((key, i) => (
                          <MenuItem key={i} value={`${key}`}>
                            {roomTypesObj[key]}
                          </MenuItem>
                        ))}
                      </Select>
                    )}

                    {(filter.column === 'bedrooms' ||
                      filter.column === 'bathrooms' ||
                      filter.column === 'accommodates' ||
                      filter.column === 'hostListingCount' ||
                      filter.column === 'minimum_nights') && (
                      <TextField
                        value={filter.value}
                        variant="outlined"
                        id="value"
                        label="Value"
                        name="value"
                        type="number"
                        size="small"
                        onChange={e => handleValueChange(e, index)}
                      />
                    )}

                    {filter.column === 'host_name' && (
                      <TextField
                        value={filter.value}
                        variant="outlined"
                        id="value"
                        label="Value"
                        name="value"
                        type="text"
                        size="small"
                        onChange={e => handleValueChange(e, index)}
                      />
                    )}
                  </FormControl>

                  {filter.column === 'coordinate' && (
                    <Button
                      className={buttonClass.locationButton}
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => handleOpen(index)}
                    >
                      {locationTriggered ? 'Reset Location' : 'Set Location'}
                    </Button>
                  )}
                </Grid>

                {index != 0 && (
                  <Grid item>
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={e => removeFilter(e, index)}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  </Grid>
                )}

                {index === 0 && (
                  <Grid item>
                    <Tooltip title="Add Filter">
                      <IconButton
                        aria-label="add-filter"
                        color="inherit"
                        size="small"
                        onClick={addFilter}
                      >
                        <AddIcon className={classes.addFilterButton} />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
            ))}

          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            // className={classes.filterContainer}
          >
            <Grid item>
              {!isDisabled && !isListingLoading ? (
                <Button
                  id="filterUpdateBtn"
                  className={buttonClass.root}
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => !isListingLoading && triggerFilter()}
                >
                  {dashboardState.showPricingDashboard
                    ? 'Create CompSet'
                    : isNoListingAvailable
                    ? 'No Listing Available'
                    : !isListingLoading
                    ? 'Update'
                    : 'Loading'}

                  {!dashboardState.showPricingDashboard && isListingLoading && (
                    <CircularProgress style={{ color: '#fff', marginRight: '10px' }} />
                  )}
                </Button>
              ) : (
                <Tooltip
                  title={
                    dashboardState.showPricingDashboard
                      ? 'Subscribe now to create CompSets'
                      : 'Subscribe now to unlock this dashboard'
                  }
                >
                  <Button
                    className={buttonClass.root}
                    size="large"
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      listings.data?.length && handleOnSampleClick();
                    }}
                  >
                    {!dashboardState.showPricingDashboard && isListingLoading ? (
                      <>
                        <CircularProgress style={{ color: '#fff', marginRight: '10px' }} /> Loading
                      </>
                    ) : (
                      <>
                        {isDisabled && (
                          <>
                            <LockSharpIcon style={{ fontSize: 22, color: '#fff' }} /> Subsribe
                          </>
                        )}
                      </>
                    )}
                  </Button>
                </Tooltip>
              )}
            </Grid>
          </Grid>

          {dashboardState.showPricingDashboard && locationTriggered && (
            <Alert severity="info" style={{ marginTop: 10 }}>
              Location restriction is set
            </Alert>
          )}
          {dashboardState.showPricingDashboard && compsetNameAlert && (
            <Alert severity="error" style={{ marginTop: 10 }}>
              'CompSet Name' field is required
            </Alert>
          )}

          {dashboardState.showPricingDashboard &&
            !compsetListingIDs.loading &&
            compsetListingIDs.data.length === 0 && (
              <Alert severity="error" style={{ marginTop: 10 }}>
                There are no listings with your current filter. Make sure to expand your filter
              </Alert>
            )}
        </Grid>
      </Drawer>

      <div>
        <Modal
          open={mapTriggered}
          onClose={handleClose}
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
        >
          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            style={{ minHeight: '100vh' }}
          >
            <div className={`${classes.paperModal} modal-custom`}>
              <Grid container direction="row" justifyContent="flex-end" alignItems="center">
                <IconButton onClick={handleClose}>
                  <CloseIcon style={{ color: COLOR_RED, fontSize: 40, marginRight: 0 }} />
                </IconButton>
              </Grid>

              <Paper className={classes.mapContainer} id="compsetMap">
                <GoogleMapReact
                  bootstrapURLKeys={{
                    key: 'AIzaSyDUnmEGqix9gziZQjtCQFJ7mCTSDV9yPDk',
                    libraries: ['drawing', 'places', 'visualization'],
                    id: 'filter-map',
                  }}
                  center={
                    locationAllowed
                      ? { lat: homeLat, lng: homeLng }
                      : { lat: 39.210878338011376, lng: -101.51684523289367 }
                  }
                  options={mapOptions}
                  zoom={locationAllowed ? 16 : 2}
                  resetBoundsOnResize
                  onChange={handleMapChange}
                  yesIWantToUseGoogleMapApiInternals
                  onGoogleApiLoaded={({ map, maps }) => apiIsLoaded(map, maps)}
                >
                  {locationPin}
                  {!bookings.loading && bookings.data.length > 0 && markers}
                </GoogleMapReact>
              </Paper>

              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                style={{ marginTop: 10 }}
              >
                <Typography>Move the map</Typography>

                <Button
                  className={buttonClass.modalButton}
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={setLocation}
                >
                  Only Include Listings In The Map
                </Button>
              </Grid>
            </div>
          </Grid>
        </Modal>
      </div>
    </>
  );
};

export default Filter;

// Filter Local Storage Map:
//---------------------------------------------------------
// Variable                             Local Storage
//---------------------------------------------------------
// Room Type					            ==		room_type
// bedroom number           			==		bedroom_number
// bathrooms number        			  ==		bathroom_number
// minimum nights             		==		minimum_nights
// Accomodates (no of people)		  ==		no_of_people
// Host Type					            ==		host_type
// Amenities					            ==		amenities { all: boolean; hot_tub: boolean; pool: boolean; }
//---------------------------------------------------------
